import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { CustomerSearchService, CustomerRosterSearchPayload } from "../../services/customer-search.service";
import { QueryBuilderFieldMeta } from "../../query-builder/query-builder.component";
import { CustomerFieldsService } from "../../services/customer-fields.service";
import { AreYouSureDialogComponent } from "../are-you-sure-dialog/are-you-sure-dialog.component";

export interface CustomerSearchDialogData {
  context?: Partial<CustomerRosterSearchPayload["context"]>;
  initialQuery?: any;
  selectedSavedId?: number;
  columns?: Array<{ id: string; name: string; type: any; group?: string }>;
  requireEmail?: boolean; // when true, enforce users with non-null email only
  forceRegisteredOnly?: boolean; // when true, exclude anonymous devices/users
}

@Component({
  selector: "lhpe-customer-search-dialog",
  templateUrl: "./customer-search-dialog.component.html",
  styleUrls: ["./customer-search-dialog.component.scss"],
})
export class CustomerSearchDialogComponent {
  form: FormGroup;
  whereText: string = "";
  savedSearches: any[] = [];
  keywordCtrl = new FormControl("");
  registrationCtrl = new FormControl("both");
  devicesCtrl = new FormControl("latest_per_user");
  includeCtrl = new FormControl([]);
  pageCtrl = new FormControl(0);
  perPageCtrl = new FormControl(50);
  nameCtrl = new FormControl("");
  descriptionCtrl = new FormControl("");
  includeNullFacilitiesCtrl = new FormControl(false);
  savedCtrl = new FormControl(null);
  queryWhere: any = null;
  columnsForBuilder: QueryBuilderFieldMeta[] = [];
  count: number | null = null;
  queryText: string = '';

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<CustomerSearchDialogComponent>,
    public searchService: CustomerSearchService,
    private customerFields: CustomerFieldsService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: CustomerSearchDialogData,
  ) {
    const base = this.searchService.buildBasePayload({
      query: data?.initialQuery || {},
    });

    this.keywordCtrl.setValue(base.query?.keyword?.value || "");
    this.registrationCtrl.setValue(base.context.mode?.registration || "both");
    this.devicesCtrl.setValue(base.context.mode?.devices || "latest_per_user");
    this.includeCtrl.setValue(base.query?.include || []);
    this.pageCtrl.setValue(base.query?.pagination?.page || 0);
    this.perPageCtrl.setValue(base.query?.pagination?.per_page || 50);
    this.includeNullFacilitiesCtrl.setValue(
      base.context.include_null_facilities === true
    );

    // If a context was provided, prefer its values for initial control state
    if (data?.context?.mode?.registration) {
      this.registrationCtrl.setValue(data.context.mode.registration);
    }
    // If forcing registered only, set and lock the control
    if (data?.forceRegisteredOnly) {
      this.registrationCtrl.setValue('registered');
      this.registrationCtrl.disable({ emitEvent: false });
    }
    if (data?.context?.mode?.devices) {
      this.devicesCtrl.setValue(data.context.mode.devices);
    }
    if (typeof (data?.context as any)?.include_null_facilities !== 'undefined') {
      this.includeNullFacilitiesCtrl.setValue(!!(data!.context as any).include_null_facilities);
    }

    this.form = this.fb.group({});
  }

  ngOnInit() {
    this.searchService.listSavedSearches().subscribe((res: any) => {
      this.savedSearches = res?.records || res || [];
      if (this.data?.selectedSavedId) {
        const selected = this.savedSearches.find((s: any) => s.id === this.data!.selectedSavedId);
        if (selected) {
          this.savedCtrl.setValue(selected);
          // Initialize form controls from the saved search's stored payload
          const q = (selected.query || {}) as CustomerRosterSearchPayload;
          const qQuery: any = (q as any).query || {};
          const qCtx: any = (q as any).context || {};
          const qMode: any = (qCtx as any).mode || {};
          this.keywordCtrl.setValue(qQuery?.keyword?.value || "");
          this.registrationCtrl.setValue(qMode?.registration || "both");
          this.devicesCtrl.setValue(qMode?.devices || "latest_per_user");
          this.includeCtrl.setValue(qQuery?.include || []);
          this.pageCtrl.setValue(qQuery?.pagination?.page ?? 0);
          this.perPageCtrl.setValue(qQuery?.pagination?.per_page ?? 50);
          if (typeof qCtx?.include_null_facilities !== 'undefined') {
            this.includeNullFacilitiesCtrl.setValue(!!qCtx.include_null_facilities);
          }
        }
      }
    });
    // Ensure query text is populated for default controls
    this.queryText = this.describeQuery();
    // Map full roster columns into query-builder metadata
    const mapType = (t: any): QueryBuilderFieldMeta['type'] => {
      const s = String(t || '').toLowerCase();
      if (s.includes('string')) return 'String';
      if (s.includes('float')) return 'Float';
      if (s.includes('integer') || s === 'int') return 'Integer';
      if (s.includes('number')) return 'Number';
      if (s.includes('boolean') || s === 'bool') return 'Boolean';
      if (s.includes('date') || s.includes('time')) return 'Date';
      if (s.endsWith('[]')) {
        if (s.startsWith('string')) return 'String[]';
        if (s.startsWith('number')) return 'Number[]';
        if (s.startsWith('integer') || s.startsWith('int')) return 'Integer[]';
        if (s.startsWith('float')) return 'Float[]';
        return 'Array';
      }
      return 'String';
    };
    const mapGroup = (g: any): any => {
      if (g === 'device' || g === 'custom' || g === 'standard') return g;
      return 'standard';
    };
    const rawCols = this.customerFields.getAllFields();
    this.columnsForBuilder = rawCols.map((c: any) => ({
      id: c.id,
      name: c.name,
      type: mapType(c.type),
      group: mapGroup(c.group)
    }));
  }

  private updateCountFromPayload(payload: CustomerRosterSearchPayload) {
    // Count via API v2 search (use per_page -1 to avoid heavy fetch)
    const quick = {
      ...payload,
      query: { ...(payload.query || {}), pagination: { page: 0, per_page: 1 } },
    } as CustomerRosterSearchPayload;
    this.searchService.runSearch(quick).subscribe((res: any) => {
      this.count = res?.count ?? null;
    });
  }

  // When builder changes, recompute count (debounce can be added later if heavy)
  ngAfterViewInit() {
    setTimeout(() => {
      const initial = this.buildPayload();
      this.updateCountFromPayload(initial);
      this.queryText = this.describeQuery();
    });

    // Live updates: watch core controls and queryBuilder output
    const recalc = () => {
      const p = this.buildPayload();
      this.updateCountFromPayload(p);
      this.queryText = this.describeQuery();
    };
    this.keywordCtrl.valueChanges.subscribe(recalc);
    this.registrationCtrl.valueChanges.subscribe(recalc);
    this.devicesCtrl.valueChanges.subscribe(recalc);
    this.includeCtrl.valueChanges.subscribe(recalc);
    this.pageCtrl.valueChanges.subscribe(recalc);
    this.perPageCtrl.valueChanges.subscribe(recalc);
    this.includeNullFacilitiesCtrl.valueChanges.subscribe(recalc);
  }
 
  onWhereChange(where: any) {
    this.queryWhere = where;
    this.updateCountFromPayload(this.buildPayload());
    this.queryText = this.describeQuery();
  }

  buildPayload(): CustomerRosterSearchPayload {
    const v = {
      keyword: this.keywordCtrl.value,
      registration: this.data?.forceRegisteredOnly ? 'registered' : this.registrationCtrl.value,
      devices: this.devicesCtrl.value,
      include: this.includeCtrl.value,
      page: this.pageCtrl.value,
      per_page: this.perPageCtrl.value,
    } as any;
    const payload = this.searchService.buildBasePayload({
      query: {
        keyword: v.keyword ? { value: v.keyword } : undefined,
        include: v.include,
        pagination: { page: v.page, per_page: v.per_page },
      },
    });

    // set/merge context defaults and any provided context hints
    payload.context.mode = payload.context.mode || {};
    payload.context.mode.registration = v.registration;
    payload.context.mode.devices = v.devices;
    if (this.data?.context) {
      if ((this.data.context as any).facility_ids) {
        payload.context.facility_ids = (this.data.context as any).facility_ids;
      }
      // Merge context.mode such that the user's chosen dialog values take precedence
      if ((this.data.context as any).mode) {
        payload.context.mode = {
          ...(this.data.context as any).mode,
          ...payload.context.mode,
        };
      }
    }

    payload.context.include_null_facilities = !!this.includeNullFacilitiesCtrl.value;

    if (this.whereText && this.whereText.trim().length) {
      try {
        (payload as any).query.where = JSON.parse(this.whereText);
      } catch (e) {
        // ignore parse error, caller can validate later
      }
    }

    if (this.queryWhere) {
      (payload as any).query.where = this.queryWhere;
    }

    // Enforce email present when required
    if (this.data?.requireEmail) {
      const emailNotNull: any = { field: 'user.email', type: 'String', op: 'not_null' };
      if ((payload as any).query.where) {
        (payload as any).query.where = { all: [ (payload as any).query.where, emailNotNull ] };
      } else {
        (payload as any).query.where = emailNotNull;
      }
    }

    return payload;
  }

  private describeQuery(): string {
    const parts: string[] = [];
    const reg = this.registrationCtrl.value;
    const devices = this.devicesCtrl.value;
    const includeNull = !!this.includeNullFacilitiesCtrl.value;
    parts.push(`Select all customers where ${reg === 'registered' ? 'registered' : reg === 'anonymous' ? 'anonymous' : 'registered or anonymous'}`);
    parts.push(includeNull ? 'include null facilities' : 'exclude null facilities');

    const where = this.queryWhere;
    const whereText = this.describeWhere(where, true);
    if (whereText) parts.push(whereText);

    return parts.join(', ');
  }

  private describeWhere(expr: any, topLevel = false): string {
    if (!expr) return '';
    if (expr.all && Array.isArray(expr.all)) {
      const items = expr.all.map((e: any) => this.describeWhere(e, false)).filter((s: string) => !!s);
      if (!items.length) return '';
      const inner = `(${items.join(' and ')})`;
      return topLevel ? `and ${inner}` : inner;
    }
    if (expr.any && Array.isArray(expr.any)) {
      const items = expr.any.map((e: any) => this.describeWhere(e, false)).filter((s: string) => !!s);
      if (!items.length) return '';
      const inner = `(${items.join(' or ')})`;
      return topLevel ? `and ${inner}` : inner;
    }
    if (expr.not) {
      const inner = this.describeWhere(expr.not, false);
      return inner ? `not (${inner})` : '';
    }
    if (expr.field) {
      const label = this.humanizeField(expr.field);
      const op = this.humanizeOp(expr.op, expr.type);
      const val = this.humanizeVal(expr.value);
      return `${label} ${op}${val ? ' ' + val : ''}`.trim();
    }
    return '';
  }

  private humanizeField(f: string): string {
    const map: any = {
      'user.first_name': 'First Name',
      'user.last_name': 'Last Name',
      'user.email': 'Email',
      'profile.loyalty.level': 'Club Level'
    };
    return map[f] || f.split('.').slice(-1)[0].replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  }
  private humanizeOp(op: string, type: string): string {
    const m: any = {
      eq: 'equals', neq: 'not equal to', contains: 'contains', starts_with: 'starts with', ends_with: 'ends with',
      gt: 'greater than', gte: 'greater than or equal to', lt: 'less than', lte: 'less than or equal to',
      is_null: 'is empty', not_null: 'is not empty', any: 'contains any of', all: 'contains all of'
    };
    return m[op] || op;
  }
  private humanizeVal(v: any): string {
    if (v == null) return '';
    if (Array.isArray(v)) return v.join(', ');
    return `'${v}'`;
  }

  apply() {
    const payload = this.buildPayload();
    this.dialogRef.close({ type: "search", payload });
  }

  cancel() {
    this.dialogRef.close();
  }

  onSavedSelected(id: number) {
    this.dialogRef.close({ type: "saved", id });
  }

  editSaved(s: any) {
    import('../save-search-dialog/save-search-dialog.component').then(({ SaveSearchDialogComponent }) => {
      const ref = this.dialog.open(SaveSearchDialogComponent, {
        width: '500px',
        data: { name: s.name || '', description: s.description || '' }
      });
      ref.afterClosed().subscribe((res: any) => {
        if (!res) { return; }
        const body = {
          name: res.name || 'Untitled Search',
          description: res.description || '',
        };
        this.searchService.updateSavedSearch(s.id, body).subscribe(() => {
          this.searchService.listSavedSearches().subscribe((r: any) => {
            this.savedSearches = r?.records || r || [];
          });
        });
      });
    });
  }

  deleteSaved(s: any) {
    const ref = this.dialog.open(AreYouSureDialogComponent, {
      width: '420px',
      data: {
        areYouSureInfo: {
          title: 'Delete saved search?',
          description: `This will permanently delete "${s.name}".`,
          continueBtn: { color: 'warn', text: 'Delete' },
          cancelBtn: { color: 'lightGrey', text: 'Cancel' }
        }
      }
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) { return; }
      this.searchService.deleteSavedSearch(s.id).subscribe(() => {
        this.savedSearches = this.savedSearches.filter((x: any) => x.id !== s.id);
      });
    });
  }

  save() {
    const payload = this.buildPayload();
    import('../save-search-dialog/save-search-dialog.component').then(({ SaveSearchDialogComponent }) => {
      const ref = this.dialog.open(SaveSearchDialogComponent, {
        width: '500px',
        data: { name: '', description: '' }
      });
      ref.afterClosed().subscribe((res: any) => {
        if (!res) { return; }
        const body = {
          name: res.name || "Untitled Search",
          description: res.description || "",
          search_type: "customer_roster",
          schema_uri: "https://lighthouse/schemas/customer-roster-search.v1.json",
          schema_version: 1,
          visibility: "private",
          tags: [],
          query: payload,
        };
        this.searchService.createSavedSearch(body).subscribe(() => {
          this.dialogRef.close({ type: "saved_created" });
        });
      });
    });
  }
}


